// Dashboard.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { 
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  VideoCameraIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  SparklesIcon,
  AcademicCapIcon,
  ClockIcon,
  TrophyIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import ChatWithSessions from '../components/Chat/ChatWithSessions';
import ImageGenerator from '../components/ImageGenerator';
import LearningAnalytics from '../components/Analytics/LearningAnalytics';
import TeacherManagementDashboard from '../components/Teacher/TeacherManagementDashboard';

const Dashboard = () => {
  const { user, isStudent, isTeacher } = useAuth();
  const [activeTab, setActiveTab] = useState('chat');

  // 탭 전환 시 모드만 변경 (스크롤 위치 유지)
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  const [stats, setStats] = useState({
    messagesCount: 0,
    imagesGenerated: 0,
    videosCreated: 0,
    studyTime: 0
  });

  useEffect(() => {
    // 컴포넌트 마운트 시 스크롤 상단으로 이동
    window.scrollTo({ top: 0, behavior: 'auto' });
    
    // 실제 사용자 통계 로드
    const loadUserStats = async () => {
      try {
        // TODO: 실제 API 연결 시 주석 해제
        // const response = await analyticsAPI.getUserStats();
        // setStats(response.data);
        
        // 현재는 기본값으로 설정
        setStats({
          messagesCount: 0,
          imagesGenerated: 0,
          videosCreated: 0,
          studyTime: 0
        });
      } catch (error) {
        console.error('통계 로드 실패:', error);
        setStats({
          messagesCount: 0,
          imagesGenerated: 0,
          videosCreated: 0,
          studyTime: 0
        });
      }
    };

    loadUserStats();
  }, []);

  const baseMenuItems = [
    {
      id: 'chat',
      name: 'AI 대화',
      icon: ChatBubbleLeftRightIcon,
      description: '안전한 AI와 대화하기',
      color: 'bg-blue-500',
      available: true
    },
    {
      id: 'image',
      name: '이미지 생성',
      icon: PhotoIcon,
      description: '교육용 이미지 만들기',
      color: 'bg-purple-500',
      available: true
    },
    {
      id: 'analytics',
      name: '사용 분석',
      icon: ChartBarIcon,
      description: '학습 현황 확인하기',
      color: 'bg-orange-500',
      available: true
    }
  ];

  // 교사인 경우 학생 모니터링 탭 추가
  const menuItems = user?.role === 'TEACHER' ? [
    ...baseMenuItems,
    {
      id: 'monitoring',
      name: '학생 모니터링',
      icon: UserGroupIcon,
      description: '학생 활동 현황 확인',
      color: 'bg-green-500',
      available: true
    }
  ] : baseMenuItems;

  const achievements = [
    { name: '첫 대화', icon: '🎉', unlocked: stats.messagesCount > 0 },
    { name: '호기심 탐험가', icon: '🔍', unlocked: stats.messagesCount >= 10 },
    { name: '창작 예술가', icon: '🎨', unlocked: stats.imagesGenerated > 0 },
    { name: '영상 크리에이터', icon: '🎬', unlocked: stats.videosCreated > 0 },
    { name: '학습 마스터', icon: '📚', unlocked: stats.studyTime >= 60 }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'chat':
        return <ChatWithSessions />;
      case 'image':
        return (
          <div className="h-full overflow-y-auto p-6">
            <ImageGenerator role={user?.role} />
          </div>
        );
      case 'analytics':
        return (
          <div className="h-full overflow-y-auto">
            <LearningAnalytics />
          </div>
        );
      case 'monitoring':
        return (
          <div className="h-full overflow-y-auto">
            <TeacherManagementDashboard />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* 상단 탭 메뉴 */}
      <div className="bg-white shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* 왼쪽: 페이지 타이틀 및 안전 모드 */}
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">학습 대시보드</h1>
                <p className="text-sm text-gray-600">{user?.username}님, 오늘도 즐거운 학습하세요!</p>
              </div>
              {/* 안전 모드 표시 */}
              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <ShieldCheckIcon className="h-4 w-4 text-green-600" />
                  <div className="flex items-center space-x-1">
                    <p className="text-xs font-medium text-green-800">안전 모드</p>
                    <p className="text-xs text-green-600">활성화</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 오른쪽: 사용자 정보 요약 */}
            <div className="flex items-center space-x-2">
              <SparklesIcon className="h-5 w-5 text-primary-500" />
              <span className="text-sm text-gray-600">
                {user?.grade && `${user.grade}학년`}
                {user?.classroomName && ` • ${user.classroomName}`}
              </span>
            </div>
          </div>
        </div>
        
        {/* 탭 메뉴 - 전체 너비 사용 */}
        <div className="border-t border-gray-200">
          <div className="flex">
            {menuItems.map((item) => (
              <motion.button
                key={item.id}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => item.available && handleTabChange(item.id)}
                disabled={!item.available}
                className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 border-b-2 transition-all ${
                  activeTab === item.id
                    ? 'border-primary-500 text-primary-600 bg-primary-50'
                    : item.available
                    ? 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    : 'border-transparent text-gray-400 cursor-not-allowed'
                }`}
              >
                <item.icon className={`h-5 w-5 ${
                  activeTab === item.id ? 'text-primary-500' : ''
                }`} />
                <span className="font-medium">{item.name}</span>
                {!item.available && (
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full ml-2">
                    준비중
                  </span>
                )}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="flex-1 overflow-hidden bg-gray-50">
        <div className="h-full">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
